import React from "react";
import * as yup from "yup";
import { FormProvider, useForm } from "react-hook-form";

import { PaziaButton, PaziaSelect } from "components/atoms";
import { COURSE_FORM_FIELD_TYPES_NAMES } from "consts";
import { PaziaModal } from "components/molecules";

import { components } from "react-select";
import { useAllCourseFormGroupsOptions } from "api/hooks";
import { CourseFromGroupApi } from "api";
import { PaziaFormButton } from "components/atoms";
import { ModalHeader } from "../Modals/ModalHeader";
import { yupResolver } from "@hookform/resolvers/yup";

interface ImportFormGroupModalProps {
  onCloseRequest: () => void;
  isOpen: boolean;
  onSubmit: (data: ImportFormGroupValues) => Promise<void>;
}

export const ImportFormGroupModal = ({ onCloseRequest, isOpen, onSubmit }: ImportFormGroupModalProps) => {
  const options = useAllCourseFormGroupsOptions(true);
  const methods = useForm<ImportFormGroupValues>({
    resolver: yupResolver(schema),
  });

  const { watch, handleSubmit } = methods;

  const selectedGroupId = watch("formGroupId");
  const selectedGroup = options.options.find((x) => x.value === selectedGroupId)?.group as CourseFromGroupApi;

  return (
    <PaziaModal id="import-formgroup-modal" onCloseRequest={onCloseRequest} isOpen={isOpen} size="xl" className="p-4">
      <FormProvider {...methods}>
        <ModalHeader>Formularz zgłoszeniowy - Import grupy</ModalHeader>
        <form className="flex flex-col justify-between" onSubmit={handleSubmit(onSubmit)}>
          <div>
            <PaziaSelect
              options={options.options}
              name="formGroupId"
              label="Grupa formularza"
              required
              components={{ Option }}
            />
            {selectedGroup ? (
              <div className="min-h-64 py-4">
                <span className="mb-1 block truncate text-sm font-medium">Lista pól</span>

                <SimpleTable
                  headers={["Nazwa", "Wymagane", "Typ"]}
                  columns={["name", "required", "type"]}
                  rows={selectedGroup.fields.map((x) => ({
                    name: x.name,
                    required: x.isRequired,
                    // @ts-ignore
                    type: COURSE_FORM_FIELD_TYPES_NAMES[x.fieldType],
                  }))}
                />
              </div>
            ) : (
              <div className="flex h-64 items-center justify-center text-center text-slate-500 dark:text-slate-300">
                Wybierz grupę formularza, którą chcesz zaimportować aby załadować szczegółowy podgląd pól.
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <PaziaButton title="Anuluj" type="tertiary" btnType="button" onClick={onCloseRequest} />
            <PaziaFormButton title="Dodaj grupę" type="success" />
          </div>
        </form>
      </FormProvider>
    </PaziaModal>
  );
};

const FORM_FIELDS = {
  FORM_GROUP_ID: "formGroupId",
} as const;

const schema = yup
  .object({
    [FORM_FIELDS.FORM_GROUP_ID]: yup.string().required(),
  })
  .required();

export type ImportFormGroupValues = yup.InferType<typeof schema>;

const Option = (props: any) => {
  const group = props.data.group as CourseFromGroupApi;

  const groupFields = group.fields?.map((field) => field.name).join(", ");

  return (
    <div className="text-sm">
      <components.Option {...props} className="!py-1">
        {group.name}
        {group.fields && <div className="text-xs text-slate-500">{groupFields}</div>}
      </components.Option>
    </div>
  );
};

interface TableProps {
  headers: string[];
  columns: string[];
  rows: Record<string, any>[];
}

const SimpleTable = ({ headers, columns, rows }: TableProps) => {
  return (
    <div className="mx-auto max-w-xl">
      <table className="border-primary-500 dark:border-primary-600 w-full border-collapse border">
        <thead>
          <tr className="bg-primary-500 dark:bg-primary-600 text-white dark:text-slate-100">
            {headers.map((header, index) => (
              <th key={index} className="px-4 py-2 text-left">
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr
              key={rowIndex}
              className="border-primary-500 dark:border-primary-600 border-b bg-white dark:bg-slate-800"
            >
              {columns.map((column, colIndex) => (
                <td key={colIndex} className="px-4 py-2">
                  {row[column]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
